import React, { useState, useMemo, useEffect, useRef } from "react"; // Added useRef
import TaskColumn from "./TaskColumn";
import TodoList from "./TodoList";
import WipeModal from "./WipeModal";
import DeadlineModule from "./DeadlineModule";
import DailyTasksModule from "./DailyTasksModule";
// Legend import was removed in previous step
import TabNavigation from "./TabNavigation";
import TodoListControls from "./TodoListControls";
import TaskControls from "./TaskControls";
import ClockManager from "./ClockManager";
import FlashCardsPage from "./flash-cards/FlashCardsPage";
import { useTaskNavigation } from "../hooks/useTaskNavigation";
import { lang } from "../lang";

function MainContent({
  tasks,
  addTask,
  updateTask,
  deleteTask,
  settings,
  todoLists,
  todoDispatch,
  saveAll,
  handleWipeAll,
  showModal,
  setShowModal,
  taskDispatch,
  dailyTasks,
  addDailyTask,
}) {
  const [activeTab, setActiveTab] = useState("focused");
  const { selectedPath, selectTask, getColumns } = useTaskNavigation(tasks);
  const columns = useMemo(() => getColumns(), [getColumns]);
  const currentYear = new Date().getFullYear();
  const firstTaskSelectedRef = useRef(false); // Ref to ensure selection happens only once

  // useEffect to select the first main task on initial load
  useEffect(() => {
    // Select the first main task if:
    // 1. Tasks are loaded (tasks array is not empty).
    // 2. No task is currently selected (selectedPath is empty).
    // 3. This auto-selection has not been performed yet in this session.
    if (
      tasks &&
      tasks.length > 0 &&
      selectedPath.length === 0 &&
      !firstTaskSelectedRef.current
    ) {
      const firstMainTask = tasks[0]; // 'tasks' prop are the top-level tasks for the main column
      if (firstMainTask) {
        selectTask(firstMainTask.id, 0); // Level 0 for main tasks
        firstTaskSelectedRef.current = true; // Mark that auto-selection has occurred
      }
    }
  }, [tasks, selectTask, selectedPath]); // Dependencies for the effect

  return (
    <div className="main-content-wrapper">
      <div className="main-content-inner">
        {settings.showClocks && <ClockManager settings={settings} />}
        <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />

        {activeTab === "focused" && (
          <>
            <TaskControls
              addTask={addTask}
              defaultTaskDuration={settings.defaultTaskDuration}
              addDailyTask={addDailyTask}
            />
            {/* Legend was removed */}
            <DeadlineModule tasks={tasks} />
            {settings.useDailyTasks && (
              <DailyTasksModule
                dailyTasks={dailyTasks}
                onUpdateTask={updateTask}
                autoCompleteOnTimer={settings.autoCompleteOnTimer}
              />
            )}
            <div className="columns">
              {columns.map((col, index) => (
                <TaskColumn
                  key={
                    index === 0
                      ? "main"
                      : selectedPath.slice(0, index).join("-")
                  }
                  level={index}
                  title={col.title}
                  tasks={col.tasks}
                  onAddTask={addTask}
                  onUpdateTask={updateTask}
                  onDeleteTask={deleteTask}
                  onSelectTask={selectTask}
                  selectedPath={selectedPath}
                  settings={settings}
                  // If the pulsating animation is strictly for "new user" guide,
                  // you might pass a prop here to indicate that context to TaskColumn/TaskItem
                  // e.g., isNewUserExperience={someConditionFromAppJs}
                />
              ))}
            </div>
          </>
        )}
        {activeTab === "todo" && (
          <>
            <TodoListControls todoDispatch={todoDispatch} />
            <div className="todo-lists">
              {(todoLists || []).map((list) => (
                <TodoList key={list.id} list={list} dispatch={todoDispatch} />
              ))}
            </div>
          </>
        )}
        {activeTab === "flashcards" && <FlashCardsPage />}

        <WipeModal
          isOpen={showModal}
          onConfirm={handleWipeAll}
          onCancel={() => setShowModal(false)}
          onSaveAll={saveAll}
        />
      </div>
      <footer className="app-footer">
        <p>
          &copy; {currentYear} PES Web Design | All Rights Reserved | This is a
          prototype application, ALL FEATURES are subject to change or deletion
          without notice.
        </p>
      </footer>
    </div>
  );
}

export default MainContent;
